/**
 * CustomerAttachmentTile - mirror of AgentAttachmentTile in
 * TicketDetailAgentPage. Renders a file row with the three
 * hover-action buttons (Download, Attach to Response, Remove) that
 * the agent surface ships. Each action gates on whether the parent
 * passed the relevant handler / capability; Remove also gates on
 * ``attachment.uploaded_by_user_id === currentUserId`` so the UI
 * never shows a button that 404s server-side.
 *
 * Pulled out into its own file so CommentBubble can render the tile
 * inline within each comment - matching the agent layout where
 * comment-bound files sit inside the comment bubble that produced
 * them.
 */
import { useState } from "react";

import { fetchCustomerAttachmentDownloadUrl } from "@/hooks/useTicketDetail";
import type { AttachmentResponse } from "@/types/api";

function formatBytes(n: number): string {
  if (n < 1024) return `${n} B`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} KB`;
  return `${(n / (1024 * 1024)).toFixed(1)} MB`;
}

function isImage(att: AttachmentResponse): boolean {
  return att.content_type.startsWith("image/");
}

export interface CustomerAttachmentTileProps {
  attachment: AttachmentResponse;
  ticketNumber: number;
  currentUserId: string | null;
  onAttachToResponse?: (attachment: AttachmentResponse) => void;
  onRemove?: (attachment: AttachmentResponse) => void;
  isAttachedToDraft?: boolean;
}

export function CustomerAttachmentTile({
  attachment,
  ticketNumber,
  currentUserId,
  onAttachToResponse,
  onRemove,
  isAttachedToDraft,
}: CustomerAttachmentTileProps): JSX.Element {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function handleDownload(): Promise<void> {
    if (isLoading) return;
    setIsLoading(true);
    setError(null);
    try {
      const url = await fetchCustomerAttachmentDownloadUrl(
        ticketNumber,
        attachment.id,
      );
      const a = document.createElement("a");
      a.href = url;
      a.target = "_blank";
      a.rel = "noopener noreferrer";
      a.download = attachment.filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
    } catch (e: unknown) {
      const message =
        e instanceof Error
          ? e.message
          : "Could not download this attachment.";
      setError(message);
    } finally {
      setIsLoading(false);
    }
  }

  const canRemove =
    onRemove !== undefined &&
    currentUserId !== null &&
    attachment.uploaded_by_user_id === currentUserId;
  const isImg = isImage(attachment);

  return (
    <div
      className="group relative flex flex-col gap-1"
      data-testid="attachment-tile"
    >
      <div className="flex items-center gap-3 rounded border border-neutral-200 bg-white px-3 py-2 pr-20 text-sm transition group-hover:bg-neutral-50">
        <i
          className={`fa-duotone fa-thin ${
            isImg ? "fa-image" : "fa-file"
          } text-2xl text-neutral-500`}
          aria-hidden="true"
        />
        <div className="min-w-0 text-left">
          <div className="truncate text-sm text-neutral-800">
            {attachment.filename}
          </div>
          <div className="text-xs text-neutral-500">
            {formatBytes(attachment.byte_size)}
            {isLoading && <span className="ml-2">opening...</span>}
          </div>
        </div>
        <div className="pointer-events-none absolute right-2 top-1/2 flex -translate-y-1/2 items-center gap-1 opacity-0 transition group-hover:pointer-events-auto group-hover:opacity-100 group-focus-within:pointer-events-auto group-focus-within:opacity-100">
          <button
            type="button"
            onClick={() => {
              void handleDownload();
            }}
            disabled={isLoading}
            title="Download"
            aria-label={`Download ${attachment.filename}`}
            className="rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 disabled:cursor-progress disabled:opacity-60"
          >
            <i
              className="fa-duotone fa-thin fa-download text-sm"
              aria-hidden="true"
            />
          </button>
          {onAttachToResponse && (
            <button
              type="button"
              onClick={() => onAttachToResponse(attachment)}
              title={
                isAttachedToDraft
                  ? "Already attached to your reply"
                  : "Attach to Response"
              }
              aria-label={
                isAttachedToDraft
                  ? "Already attached to your reply"
                  : `Attach ${attachment.filename} to your reply`
              }
              disabled={isAttachedToDraft}
              className="rounded p-1 text-neutral-500 hover:bg-neutral-200 hover:text-neutral-800 disabled:cursor-not-allowed disabled:text-blue-600 disabled:opacity-100"
              data-testid="attachment-attach-to-response"
            >
              <i
                className={`fa-duotone fa-thin ${
                  isAttachedToDraft ? "fa-check" : "fa-paperclip"
                } text-sm`}
                aria-hidden="true"
              />
            </button>
          )}
          {canRemove && (
            <button
              type="button"
              onClick={() => onRemove?.(attachment)}
              title="Remove"
              aria-label={`Remove ${attachment.filename}`}
              className="rounded p-1 text-neutral-500 hover:bg-red-100 hover:text-red-700"
              data-testid="attachment-remove"
            >
              <i
                className="fa-duotone fa-thin fa-trash text-sm"
                aria-hidden="true"
              />
            </button>
          )}
        </div>
      </div>
      {error && <p className="text-xs text-red-600">{error}</p>}
    </div>
  );
}

export default CustomerAttachmentTile;
